import { z } from "zod";
import type { Operation } from "../../types";
import { crmIdParams } from "./_shared";

// list is the only read; every template write mutates the collection.

const templateSchema = z.object({
  id: z.number(),
  workspace_id: z.number(),
  slug: z.string(),
  name: z.string(),
  subject: z.string(),
  body_text: z.string(),
  body_html: z.string().nullable(),
  variables_json: z.string(),
  shared: z.number(),
  owner_actor_id: z.number().nullable(),
});

const list: Operation<void, void, void, { templates: z.infer<typeof templateSchema>[] }> = {
  method: "GET",
  path: () => "/api/crm/templates",
  response: z.object({ templates: z.array(templateSchema) }),
};

const create: Operation<
  void,
  void,
  {
    slug: string;
    name: string;
    subject: string;
    body_text: string;
    body_html?: string | null;
    shared?: boolean;
  },
  z.infer<typeof templateSchema>
> = {
  method: "POST",
  path: () => "/api/crm/templates",
  body: z.object({
    slug: z.string().min(1),
    name: z.string().min(1),
    subject: z.string().min(1),
    body_text: z.string().min(1),
    body_html: z.string().nullable().optional(),
    shared: z.boolean().optional(),
  }),
  response: templateSchema,
  invalidates: [list],
};

// invalidation-ignore: write-nothing POST — pure template render, no vault write
const render: Operation<
  { id: number },
  void,
  { lead_id?: number; ctx?: Record<string, unknown> },
  { subject: string; body_text: string; body_html: string | null }
> = {
  method: "POST",
  path: ({ id }) => `/api/crm/templates/${id}/render`,
  params: crmIdParams,
  body: z.object({
    lead_id: z.number().int().optional(),
    ctx: z.record(z.string(), z.unknown()).optional(),
  }),
  response: z.object({
    subject: z.string(),
    body_text: z.string(),
    body_html: z.string().nullable(),
  }),
};

const update: Operation<
  { id: number },
  void,
  {
    name?: string;
    subject?: string;
    body_text?: string;
    body_html?: string | null;
    shared?: boolean;
  },
  { ok: true }
> = {
  method: "PATCH",
  path: ({ id }) => `/api/crm/templates/${id}`,
  params: crmIdParams,
  body: z.object({
    name: z.string().min(1).optional(),
    subject: z.string().min(1).optional(),
    body_text: z.string().min(1).optional(),
    body_html: z.string().nullable().optional(),
    shared: z.boolean().optional(),
  }),
  response: z.object({ ok: z.literal(true) }),
  invalidates: [list],
};

const archive: Operation<{ id: number }, void, void, { ok: true }> = {
  method: "DELETE",
  path: ({ id }) => `/api/crm/templates/${id}`,
  params: crmIdParams,
  response: z.object({ ok: z.literal(true) }),
  invalidates: [list],
};

export const templates = { list, create, update, archive, render };
